import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Profile } from '@isbd/shared';
import { getOAuthRedirectUrl } from '@/lib/nativeApp';
import { kuunda, type Session, type User } from '@/lib/kuunda';
import { findReferrerProfileId } from '@/features/auth/referral';
import { type EnsureProfileExtras, resolveUserProfile } from '@/features/auth/profile';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<Session>;
  signUp: (input: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isStaff: boolean;
  isAdmin: boolean;
}

export interface SignUpInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  countryCode?: string;
  referralToken?: string;
}

const AuthContext = createContext<AuthState | null>(null);

/** Diffère le travail async hors du callback auth pour éviter un deadlock du lock auth (surtout WebView mobile). */
function deferAuthSideEffect(task: () => void) {
  window.setTimeout(task, 0);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  /** true tant que la session existe mais le profil n’est pas encore résolu */
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProfileForUser = useCallback(async (user: User, extras?: EnsureProfileExtras) => {
    setProfileLoading(true);
    try {
      const nextProfile = await resolveUserProfile(user, extras);
      setProfile(nextProfile);
      if (nextProfile) void kuunda.rpc('touch_last_seen');
      return nextProfile;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    await kuunda.auth.initialize();
    const current = kuunda.auth.getSession();
    const user = current?.user;
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    await loadProfileForUser(user);
  }, [loadProfileForUser]);

  useEffect(() => {
    let mounted = true;

    void kuunda.auth.initialize().then(() => {
      if (!mounted) return;
      const current = kuunda.auth.getSession();
      setSession(current);
      setLoading(false);
      if (current?.user) {
        deferAuthSideEffect(() => {
          if (!mounted) return;
          void loadProfileForUser(current.user);
        });
      } else {
        setProfileLoading(false);
      }
    });

    const { data: sub } = kuunda.auth.onAuthStateChange(({ event, session: next }) => {
      if (event === 'PASSWORD_RECOVERY') {
        try {
          sessionStorage.setItem('isbd_password_recovery', '1');
        } catch {
          /* ignore */
        }
      }
      setSession(next);
      setLoading(false);
      if (next?.user) {
        deferAuthSideEffect(() => {
          if (!mounted) return;
          void loadProfileForUser(next.user);
        });
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfileForUser]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await kuunda.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (!data.session) throw new Error('Session introuvable après connexion.');
      setSession(data.session);
      await loadProfileForUser(data.session.user);
      return data.session;
    },
    [loadProfileForUser],
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const emailRedirectTo = getOAuthRedirectUrl();
      const { data, error } = await kuunda.auth.signUp({
        email: input.email,
        password: input.password,
        options: {
          emailRedirectTo,
          data: {
            first_name: input.firstName,
            last_name: input.lastName,
            phone_number: input.phoneNumber ?? '',
            country_code: input.countryCode ?? '',
            referral_token: input.referralToken ?? '',
          },
        },
      });
      if (error) throw error;
      if (data.session?.user) {
        let referredBy: string | null = null;
        if (input.referralToken) {
          referredBy = await findReferrerProfileId(input.referralToken);
          if (referredBy === data.session.user.id) referredBy = null;
        }
        setSession(data.session);
        await loadProfileForUser(data.session.user, {
          firstName: input.firstName,
          lastName: input.lastName,
          phoneNumber: input.phoneNumber,
          countryCode: input.countryCode,
          referredBy,
        });
      }
    },
    [loadProfileForUser],
  );

  const signOut = useCallback(async () => {
    const { error } = await kuunda.auth.signOut();
    if (error) throw error;
    setSession(null);
    setProfile(null);
    setProfileLoading(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      profile,
      loading,
      profileLoading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      isStaff: profile?.role === 'admin' || profile?.role === 'advisor' || profile?.role === 'jury',
      isAdmin: profile?.role === 'admin',
    }),
    [session, profile, loading, profileLoading, signIn, signUp, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
}
